/** MI DOMINO — 🎯 Daily missions.
 *  Three missions a day (same three for everyone — seeded by the date), each
 *  paying PhilCoins when done. Progress is measured as the DELTA of the
 *  player's own stats since the day started, so nothing in the game loop had
 *  to change: every saveStats() call ticks the missions. Auto-claimed: the
 *  coins land the moment the last hand/match/capicú completes, with a toast. */
import { STATS } from '../state';
import { LANG } from '../ui/i18n';
import { earnCoins } from '../game/engine';
import { toast } from '../ui/toast';

export interface Quest {
  id: string; stat: string; n: number; coins: number;
  es: string; en: string; icon: string; easy?: boolean;
}

export const QUEST_POOL: Quest[] = [
  { id: 'hands3', stat: 'hands', n: 3, coins: 30, es: 'Juega 3 manos', en: 'Play 3 hands', icon: '✋', easy: true },
  { id: 'hands6', stat: 'hands', n: 6, coins: 60, es: 'Juega 6 manos', en: 'Play 6 hands', icon: '✋', easy: true },
  { id: 'handswon2', stat: 'handsWon', n: 2, coins: 40, es: 'Gana 2 manos', en: 'Win 2 hands', icon: '🏅', easy: true },
  { id: 'match1', stat: 'matches', n: 1, coins: 50, es: 'Termina una partida', en: 'Finish a match', icon: '🎲' },
  { id: 'matchwin1', stat: 'matchesWon', n: 1, coins: 80, es: 'Gana una partida', en: 'Win a match', icon: '🏆' },
  { id: 'capicu1', stat: 'capicus', n: 1, coins: 60, es: 'Haz un capicú', en: 'Make a capicú', icon: '🎯' },
  { id: 'tranque1', stat: 'tranquesWon', n: 1, coins: 60, es: 'Gana un tranque', en: 'Win a tranque', icon: '🔒' },
  { id: 'chuchazo1', stat: 'chuchazos', n: 1, coins: 100, es: 'Pega un chuchazo', en: 'Hit a chuchazo', icon: '💥' },
  { id: 'online1', stat: 'onlineHands', n: 1, coins: 50, es: 'Juega una mano online', en: 'Play an online hand', icon: '🌐' },
];

interface QState { day: string; ids: string[]; base: Record<string, number>; done: Record<string, boolean>; }
let Q: QState = { day: '', ids: [], base: {}, done: {} };
try { Q = { ...Q, ...JSON.parse(localStorage.getItem('mdquests') || '{}') }; } catch { /* no storage */ }

const save = (): void => { try { localStorage.setItem('mdquests', JSON.stringify(Q)); } catch { /* */ } };
const todayKey = (): string => {
  const d = new Date();   // local day — the player's midnight, not UTC's
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const statOf = (k: string): number => Number((STATS as any)[k] || 0);
const snapshot = (): Record<string, number> => {
  const o: Record<string, number> = {};
  for (const q of QUEST_POOL) o[q.stat] = statOf(q.stat);
  return o;
};

/** Same three missions for everyone on a given day: one easy + two others. */
export function pickFor(day: string): string[] {
  let seed = 0;
  for (const ch of day) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const rnd = (): number => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  const easy = QUEST_POOL.filter((q) => q.easy), rest = QUEST_POOL.filter((q) => !q.easy);
  const out = [easy[Math.floor(rnd() * easy.length)].id];
  const pool = rest.slice();
  while (out.length < 3 && pool.length) out.push(pool.splice(Math.floor(rnd() * pool.length), 1)[0].id);
  return out;
}

/** New day → new missions, progress measured from this moment's stats. */
export function questsInit(): void {
  const day = todayKey();
  if (Q.day === day && Q.ids.length === 3) return;
  Q = { day, ids: pickFor(day), base: snapshot(), done: {} };
  save();
}

export interface QuestView extends Quest { prog: number; done: boolean; }
export function questList(): QuestView[] {
  questsInit();
  return Q.ids.map((id) => {
    const q = QUEST_POOL.find((x) => x.id === id)!;
    const prog = Math.min(q.n, Math.max(0, statOf(q.stat) - (Q.base[q.stat] || 0)));
    return { ...q, prog, done: !!Q.done[id] || prog >= q.n };
  });
}

/** Called after every stats save: pays out whatever just got completed. */
export function questsTick(): void {
  questsInit();
  for (const q of questList()) {
    if (Q.done[q.id] || q.prog < q.n) continue;
    Q.done[q.id] = true;
    save();
    const label = LANG === 'en' ? q.en : q.es;
    earnCoins(q.coins, (LANG === 'en' ? 'mission: ' : 'misión: ') + label + ' ' + q.icon);
    try { toast(`🎯 ${label} ✓  +${q.coins} 🪙`); } catch { /* toast not ready */ }
  }
  try { renderLobbyQuests(); } catch { /* lobby not on screen */ }
}

/** Card body: header + three rows with progress bars. */
export function questsHTML(compact = false): string {
  const en = LANG === 'en';
  const list = questList();
  const doneN = list.filter((q) => q.done).length;
  const rows = list.map((q) => `<div class="qrow${q.done ? ' done' : ''}">
      <span class="qicon">${q.done ? '✅' : q.icon}</span>
      <div class="qbody">
        <div class="qname">${en ? q.en : q.es}</div>
        <div class="qbar"><i style="width:${Math.round(q.prog / q.n * 100)}%"></i></div>
      </div>
      <span class="qprog">${q.prog}/${q.n}</span>
      <span class="qcoins">+${q.coins} 🪙</span>
    </div>`).join('');
  return `<div class="quests${compact ? ' compact' : ''}">
    <div class="qhead"><b>🎯 ${en ? "Today's missions" : 'Misiones de hoy'}</b><small>${doneN}/3 · ${en ? 'new ones at midnight' : 'nuevas a medianoche'}</small></div>
    ${rows}
  </div>`;
}

/** The lobby shows the missions under the play buttons (re-rendered on tick / language). */
export function renderLobbyQuests(): void {
  const el = document.getElementById('lobby-quests');
  if (el) el.innerHTML = questsHTML(true);
}
